import React, { useState, useEffect } from 'react';
import { resourcesApi, blogApi, academyApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    ChevronLeft, ExternalLink, BookOpen, Users, Building, Wrench,
    GraduationCap, FileText, Clock, Play, Lock, Crown
} from 'lucide-react';

// Resources Page
export const Resources = ({ onBack }) => {
    const [resources, setResources] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');

    useEffect(() => {
        loadResources();
    }, []);

    const loadResources = async () => {
        try {
            const response = await resourcesApi.list();
            setResources(response.data);
        } catch (error) {
            console.error('Failed to load resources');
        }
    };

    const categories = [
        { id: 'all', name: 'All', icon: <BookOpen className="h-4 w-4" /> },
        { id: 'editors', name: 'Editors', icon: <Users className="h-4 w-4" /> },
        { id: 'coaches', name: 'Coaches', icon: <Users className="h-4 w-4" /> },
        { id: 'publishers', name: 'Publishers', icon: <Building className="h-4 w-4" /> },
        { id: 'writing-groups', name: 'Writing Groups', icon: <Users className="h-4 w-4" /> },
        { id: 'tools', name: 'Tools', icon: <Wrench className="h-4 w-4" /> },
    ];

    const filteredResources = activeCategory === 'all' 
        ? resources 
        : resources.filter(r => r.category === activeCategory);

    // Default resources if none exist
    const defaultResources = [
        { id: '1', name: 'Reedsy', url: 'https://reedsy.com', category: 'editors', description: 'Find professional editors, designers, and marketers for your book.' },
        { id: '2', name: 'Scribophile', url: 'https://scribophile.com', category: 'writing-groups', description: 'Online writing community with critique groups and workshops.' },
        { id: '3', name: 'Jane Friedman', url: 'https://janefriedman.com', category: 'coaches', description: 'Publishing industry insights and guidance for authors.' },
        { id: '4', name: 'QueryTracker', url: 'https://querytracker.net', category: 'publishers', description: 'Database of literary agents and publishers accepting submissions.' },
        { id: '5', name: 'ProWritingAid', url: 'https://prowritingaid.com', category: 'tools', description: 'Grammar checker and style editor for writers.' },
        { id: '6', name: 'Fictionary', url: 'https://fictionary.co', category: 'tools', description: 'Story editing software based on story elements.' },
        { id: '7', name: 'NaNoWriMo', url: 'https://nanowrimo.org', category: 'writing-groups', description: 'National Novel Writing Month community and resources.' },
        { id: '8', name: 'Writer\'s Digest', url: 'https://writersdigest.com', category: 'coaches', description: 'Writing tips, publishing advice, and competitions.' },
    ];

    const displayResources = filteredResources.length > 0 ? filteredResources : 
        defaultResources.filter(r => activeCategory === 'all' || r.category === activeCategory);

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <h1 className="font-serif text-2xl font-semibold">Writer Resources</h1>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                <p className="text-muted-foreground mb-8">
                    Curated links to editors, coaches, publishers, writing groups, and tools to help your writing journey.
                </p>

                {/* Category Tabs */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {categories.map((cat) => (
                        <Button
                            key={cat.id}
                            variant={activeCategory === cat.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setActiveCategory(cat.id)}
                            data-testid={`category-${cat.id}`}
                        >
                            {cat.icon}
                            <span className="ml-2">{cat.name}</span>
                        </Button>
                    ))}
                </div>

                {/* Resources Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                    {displayResources.map((resource) => (
                        <a 
                            key={resource.id}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="card-default p-4 hover-lift group"
                            data-testid={`resource-${resource.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="font-serif font-semibold group-hover:text-accent transition-colors">
                                        {resource.name}
                                    </h3>
                                    <Badge variant="outline" className="mt-1">{resource.category}</Badge>
                                </div>
                                <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors" />
                            </div>
                            {resource.description && (
                                <p className="text-sm text-muted-foreground mt-3">{resource.description}</p>
                            )}
                        </a>
                    ))}
                </div>
            </main>
        </div>
    );
};

// Blog Page
export const Blog = ({ onBack }) => {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        loadPosts();
    }, []);

    const loadPosts = async () => {
        try {
            const response = await blogApi.list();
            setPosts(response.data);
        } catch (error) {
            console.error('Failed to load posts');
        }
    };

    // Default blog posts if none exist
    const defaultPosts = [
        {
            id: '1',
            title: 'Getting Started with eSkrybe',
            excerpt: 'Learn how to set up your first book project and make the most of our writing tools.',
            category: 'tutorials',
            created_at: new Date().toISOString(),
        },
        {
            id: '2',
            title: 'Using AI to Overcome Writer\'s Block',
            excerpt: 'Discover how AI writing assistance can help you break through creative barriers.',
            category: 'writing-tips',
            created_at: new Date().toISOString(),
        },
        {
            id: '3',
            title: 'Creating Consistent Character Illustrations',
            excerpt: 'Tips for using AI illustration to maintain visual consistency throughout your book.',
            category: 'tutorials',
            created_at: new Date().toISOString(),
        },
    ];

    const displayPosts = posts.length > 0 ? posts : defaultPosts;

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <h1 className="font-serif text-2xl font-semibold">Blog</h1>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-8">
                <div className="space-y-6">
                    {displayPosts.map((post) => (
                        <article 
                            key={post.id}
                            className="card-default p-6 hover-lift cursor-pointer"
                            data-testid={`post-${post.id}`}
                        >
                            <Badge variant="outline" className="mb-3">{post.category}</Badge>
                            <h2 className="font-serif text-xl font-semibold mb-2">{post.title}</h2>
                            {post.excerpt && (
                                <p className="text-muted-foreground">{post.excerpt}</p>
                            )}
                            <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                                <FileText className="h-4 w-4" />
                                <span>{new Date(post.created_at).toLocaleDateString()}</span>
                            </div>
                        </article>
                    ))}
                </div>
            </main>
        </div>
    );
};

// Academy Page with Video Player
export const Academy = ({ onBack, onNavigate }) => {
    const { user, hasFullAccess, isDemo, isDemoExpired } = useAuth();
    const [lessons, setLessons] = useState([]);
    const [activeModule, setActiveModule] = useState('all');
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingVideo, setGeneratingVideo] = useState(null);
    const [subscriptionRequired, setSubscriptionRequired] = useState(false);

    // Check if user has access (paid subscription OR demo user)
    const hasSubscription = hasFullAccess;

    useEffect(() => {
        loadLessons();
    }, []);

    const loadLessons = async () => {
        setLoading(true);
        try {
            const response = await academyApi.lessons();
            setLessons(response.data.lessons || []);
        } catch (error) {
            console.error('Failed to load lessons');
        } finally {
            setLoading(false);
        }
    };

    const loadLessonDetail = async (lessonId) => {
        if (!hasSubscription) {
            setSubscriptionRequired(true);
            return;
        }
        try {
            const response = await academyApi.lesson(lessonId);
            setSelectedLesson(response.data);
            setSubscriptionRequired(false);
        } catch (error) {
            if (error.response?.status === 403) {
                setSubscriptionRequired(true);
            } else {
                console.error('Failed to load lesson');
            }
        }
    };

    const handleGenerateVideo = async (lessonId) => {
        setGeneratingVideo(lessonId);
        try {
            const response = await academyApi.generateVideo(lessonId);
            // Update the lesson with the video URL
            setLessons(prev => prev.map(l => 
                l.id === lessonId 
                    ? { ...l, video_status: 'ready', video_url: response.data.video_url }
                    : l
            ));
            if (selectedLesson?.id === lessonId) {
                setSelectedLesson(prev => ({ ...prev, video_status: 'ready', video_url: response.data.video_url }));
            }
        } catch (error) {
            console.error('Failed to generate video:', error);
        } finally {
            setGeneratingVideo(null);
        }
    };

    const modules = [
        { id: 'all', name: 'All Lessons' },
        { id: 'fundamentals', name: 'Writing Fundamentals' },
        { id: 'story-structure', name: 'Story Structure' },
        { id: 'characters', name: 'Character Development' },
        { id: 'worldbuilding', name: 'Worldbuilding' },
        { id: 'editing', name: 'Editing & Revision' },
        { id: 'publishing', name: 'Publishing' },
    ];

    const filteredLessons = activeModule === 'all' 
        ? lessons 
        : lessons.filter(l => l.module === activeModule);

    // Lesson Detail View
    if (selectedLesson) {
        return (
            <div className="min-h-screen bg-background">
                <header className="border-b border-border">
                    <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                        <Button variant="ghost" onClick={() => setSelectedLesson(null)} data-testid="back-to-lessons">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Lessons
                        </Button>
                    </div>
                </header>

                <main className="max-w-4xl mx-auto px-6 py-8">
                    <Badge variant="outline" className="mb-4">{selectedLesson.module}</Badge>
                    <h1 className="font-serif text-3xl font-semibold mb-2">{selectedLesson.title}</h1>
                    <p className="text-muted-foreground mb-6">{selectedLesson.description}</p>
                    
                    <div className="flex items-center gap-4 mb-8 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{selectedLesson.duration_minutes} min</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <GraduationCap className="h-4 w-4" />
                            <span>Video Lesson</span>
                        </div>
                    </div>

                    {/* Video Player Area */}
                    <div className="mb-8">
                        {selectedLesson.video_status === 'ready' && selectedLesson.video_url ? (
                            <div className="aspect-video rounded-xl overflow-hidden bg-black">
                                <video 
                                    controls 
                                    className="w-full h-full"
                                    data-testid="lesson-video"
                                >
                                    <source src={`${process.env.REACT_APP_BACKEND_URL}${selectedLesson.video_url}`} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ) : (
                            <div className="aspect-video rounded-xl bg-gradient-to-br from-accent/10 to-accent/5 border border-border flex flex-col items-center justify-center">
                                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center text-accent mb-4">
                                    <Clock className="h-8 w-8" />
                                </div>
                                <h3 className="font-semibold mb-2">Video Coming Soon</h3>
                                <p className="text-sm text-muted-foreground text-center max-w-md px-4">
                                    The video lesson for this topic is being produced. In the meantime, please read the full lesson transcript below.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Lesson Script/Transcript */}
                    {selectedLesson.script && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Lesson Transcript
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    {selectedLesson.script.split('\n\n').map((paragraph, i) => (
                                        <p key={i} className="text-muted-foreground mb-4">{paragraph}</p>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Buy Credits CTA */}
                    <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold mb-1">Unlock More AI Features</h3>
                                <p className="text-sm text-muted-foreground">Get credits for AI writing assistance, illustrations, and more</p>
                            </div>
                            <Button onClick={() => onNavigate && onNavigate('credits')} className="bg-accent hover:bg-accent/90" data-testid="academy-buy-credits">
                                Buy Credits
                            </Button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // Subscription Required View
    if (subscriptionRequired) {
        return (
            <div className="min-h-screen bg-background">
                <header className="border-b border-border">
                    <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                        <Button variant="ghost" onClick={() => setSubscriptionRequired(false)} data-testid="back-to-lessons">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back to Lessons
                        </Button>
                    </div>
                </header>
                
                <main className="max-w-2xl mx-auto px-6 py-16 text-center">
                    <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
                        <Lock className="h-10 w-10 text-accent" />
                    </div>
                    <h1 className="font-serif text-3xl font-semibold mb-4">Unlock the Writing Mastery Academy</h1>
                    <p className="text-lg text-muted-foreground mb-8">
                        Get unlimited access to all video lessons, expert writing techniques, and exclusive content with any paid subscription.
                    </p>
                    
                    <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
                        <h2 className="font-semibold mb-4 flex items-center justify-center gap-2">
                            <Crown className="h-5 w-5 text-accent" />
                            Subscriber Benefits
                        </h2>
                        <ul className="text-left space-y-2 text-muted-foreground max-w-md mx-auto">
                            <li className="flex items-center gap-2">
                                <GraduationCap className="h-4 w-4 text-accent flex-shrink-0" />
                                10 expert video lessons on writing craft
                            </li>
                            <li className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-accent flex-shrink-0" />
                                Full lesson transcripts and notes
                            </li>
                            <li className="flex items-center gap-2">
                                <Play className="h-4 w-4 text-accent flex-shrink-0" />
                                Watch anytime, at your own pace
                            </li>
                            <li className="flex items-center gap-2">
                                <BookOpen className="h-4 w-4 text-accent flex-shrink-0" />
                                New lessons added regularly
                            </li>
                        </ul>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            className="bg-accent hover:bg-accent/90 text-lg px-8 py-6"
                            onClick={() => onNavigate && onNavigate('landing')}
                            data-testid="view-plans-btn"
                        >
                            <Crown className="h-5 w-5 mr-2" />
                            View Subscription Plans
                        </Button>
                        <Button 
                            variant="outline"
                            className="text-lg px-8 py-6"
                            onClick={() => setSubscriptionRequired(false)}
                        >
                            Browse Lessons
                        </Button>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mt-6">
                        Plans start at just $12/month (billed annually)
                    </p>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Back
                    </Button>
                    <div>
                        <h1 className="font-serif text-2xl font-semibold">Writing Mastery Academy</h1>
                        <p className="text-sm text-muted-foreground">Learn from expert writing techniques with AI-powered video lessons</p>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-8">
                {/* Demo User Notice */}
                {isDemo && (
                    <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <div className="flex items-center gap-3">
                            <Crown className="h-5 w-5 text-amber-500" />
                            <div>
                                <p className="font-medium text-amber-600">Demo Access Active</p>
                                <p className="text-sm text-muted-foreground">
                                    You have full access to Academy content. 
                                    {isDemoExpired ? (
                                        <span className="text-red-500"> Your demo has expired - upgrade to keep access!</span>
                                    ) : user?.demo_expires_at && (
                                        <span> Expires {new Date(user.demo_expires_at).toLocaleDateString()}</span>
                                    )}
                                </p>
                            </div>
                            <Button 
                                size="sm" 
                                className="ml-auto"
                                onClick={() => onNavigate && onNavigate('landing')}
                            >
                                Upgrade Now
                            </Button>
                        </div>
                    </div>
                )}

                {/* Instructor Banner */}
                <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-accent/10 via-accent/5 to-transparent border border-accent/20">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
                            <GraduationCap className="h-8 w-8 text-accent" />
                        </div>
                        <div>
                            <h2 className="font-serif text-xl font-semibold">Your Personal Writing Coach</h2>
                            <p className="text-muted-foreground">Video lessons featuring expert instruction on craft, structure, and publishing</p>
                        </div>
                    </div>
                </div>

                {/* Module Tabs */}
                <div className="flex flex-wrap gap-2 mb-8">
                    {modules.map((mod) => (
                        <Button
                            key={mod.id}
                            variant={activeModule === mod.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setActiveModule(mod.id)}
                            data-testid={`module-${mod.id}`}
                        >
                            {mod.name}
                        </Button>
                    ))}
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-pulse text-muted-foreground">Loading lessons...</div>
                    </div>
                ) : (
                    /* Lessons Grid */
                    <div className="grid md:grid-cols-2 gap-4">
                        {filteredLessons.map((lesson) => (
                            <div 
                                key={lesson.id}
                                className="card-default p-4 hover-lift cursor-pointer group"
                                onClick={() => loadLessonDetail(lesson.id)}
                                data-testid={`lesson-${lesson.id}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent flex-shrink-0 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                                        {hasSubscription ? (
                                            lesson.video_status === 'ready' ? (
                                                <Play className="h-5 w-5" />
                                            ) : (
                                                <Play className="h-5 w-5 opacity-50" />
                                            )
                                        ) : (
                                            <Lock className="h-5 w-5" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge variant="outline">{lesson.module}</Badge>
                                            {lesson.video_status === 'ready' && hasSubscription && (
                                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Video Ready</Badge>
                                            )}
                                            {!hasSubscription && (
                                                <Badge className="bg-accent/10 text-accent border-accent/20">Subscriber Only</Badge>
                                            )}
                                        </div>
                                        <h3 className="font-serif font-semibold mb-1">{lesson.title}</h3>
                                        {lesson.description && (
                                            <p className="text-sm text-muted-foreground line-clamp-2">{lesson.description}</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                                            <Clock className="h-3 w-3" />
                                            <span>{lesson.duration_minutes} min</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredLessons.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">No lessons found in this category</p>
                    </div>
                )}
            </main>
        </div>
    );
};

export default Resources;
